/**
 * CategorySection — Plan 04-03 / UI-SPEC §"<CategorySection />".
 *
 * One category's unchecked rows. Per UI-SPEC §"Empty section: hidden
 * entirely" — callers should not pass an empty `items` array (route filters
 * via `groupItems()` first).
 *
 * #157 (redesign steg 7d, design/screens/Stell_Handle.dc.html): kortet er
 * skissens glasskort (radius 24, 6/6/6/14 px), og overskriften er en
 * versal-etikett «FRUKT OG GRØNT · 2» i ink-2. Fargeprikkene er borte fra
 * kortet — skissen har dem ikke, og navnet bærer kategorien alene.
 */
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import type { ShoppingItem } from '@stell/shared';
import { GLASS_SURFACE } from '../../components/primitives/Card';
import type { ColorSlot } from '../../components/primitives/PixelAvatar';
import { ItemRow } from './ItemRow';
import type { GroupedSectionKey } from './useShoppingList';

/** Glasskortet rundt en gruppe rader — delt med «Avkrysset»-lista i ruten. */
export const SHOPPING_CARD = `${GLASS_SURFACE} rounded-lg py-1.5 pr-1.5 pl-3.5`;

interface CategorySectionProps {
  sectionKey: GroupedSectionKey;
  items: ShoppingItem[];
  onOpenOverride: (item: ShoppingItem) => void;
  /** Map of memberId → {short_name, color_token} for the attribution chip. */
  membersById?: Record<string, { short_name: string; color_token: ColorSlot }> | undefined;
  pendingIds?: ReadonlySet<string> | undefined;
}

export function CategorySection({
  sectionKey,
  items,
  onOpenOverride,
  membersById,
  pendingIds,
}: CategorySectionProps) {
  const { t } = useTranslation();
  const label = t(`shopping.category.${sectionKey}`);
  const titleId = useId();

  return (
    <section
      data-testid={`shopping-section-${sectionKey}`}
      aria-labelledby={titleId}
      className={SHOPPING_CARD}
    >
      <header data-testid={`shopping-section-header-${sectionKey}`} className="mt-1.5 mb-0.5">
        <h2 id={titleId} className="m-0 text-caption font-extrabold tracking-wide text-ink-2 uppercase">
          {label} · <span className="tnum">{items.length}</span>
        </h2>
      </header>
      <ul className="m-0 flex list-none flex-col p-0">
        {items.map((it) => (
          <li key={it.id}>
            <ItemRow
              item={it}
              onOpenOverride={onOpenOverride}
              pending={pendingIds?.has(it.id)}
              addedByMember={membersById?.[it.added_by]}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}
